import { ApiError } from '@api/client'
import type { PointOfInterest, StageLocality } from '@api/types'
import { mapHrefForStage } from '@features/map/focus'
import { useI18n } from '@i18n/I18nProvider'
import { formatKm } from '@i18n/locale'
import { ButtonLink } from '@ui/Button'
import { ExternalLink } from '@ui/ExternalLink'
import { Icon } from '@ui/Icon'
import { Photo } from '@ui/Photo'
import { Screen } from '@ui/Screen'
import { ErrorState, LoadingState } from '@ui/States'
import { Link } from 'wouter'
import { StageDone } from './StageDone'
import { useMyStages } from './useMyStages'
import { useStage } from './useStages'

// Ficha de etapa (spec `route`): resumen, marcarla como completada, las localidades que atraviesa (con su
// patrimonio y tradiciones) y sus lugares de interés. Para ampliar, se enlaza a Visit.
export const StageDetailPage = ({ id }: { id: string }) => {
	const { t, locale } = useI18n()
	const { data: stage, error, isPending, refetch } = useStage(id)
	const { mine, toggle } = useMyStages()

	if (isPending)
		return (
			<Screen>
				<LoadingState shape="detail" />
			</Screen>
		)
	if (error instanceof ApiError && error.isNotFound)
		return (
			<Screen backHref="/etapas">
				<div role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
					<Icon name="route" className="size-12 text-earth-600" />
					<p className="text-lg">{t('stages.notFound')}</p>
				</div>
			</Screen>
		)
	if (error || !stage) return <ErrorState error={error} onRetry={() => refetch()} />

	const cover = stagePhoto(stage.pointsOfInterest)
	return (
		<Screen backHref="/etapas">
			{cover && <Photo src={cover} alt="" className="mb-4 aspect-[16/9] w-full rounded-3xl object-cover" />}
			<p className="font-semibold text-earth-800">
				{t('stages.number', { n: stage.order })} ·{' '}
				{t('stages.distance', { km: formatKm(stage.distanceKm, locale) })}
			</p>
			<h1 className="mb-3 text-3xl leading-tight">{stage.name}</h1>
			<p className="mb-4 text-lg">{stage.summary}</p>
			<div className="mb-4">
				<ButtonLink href={mapHrefForStage(stage.id)} variant="secondary">
					<Icon name="map" />
					{t('stages.viewOnMap')}
				</ButtonLink>
			</div>
			<StageDone doneOn={mine[stage.id]} onToggle={() => toggle(stage.id)} />
			{stage.visitUrl && (
				<div className="mt-2">
					<ExternalLink href={stage.visitUrl}>{t('stages.moreInfo')}</ExternalLink>
				</div>
			)}

			{stage.localities.length > 0 && (
				<section className="mt-8">
					<h2 className="mb-3 text-2xl font-semibold">{t('stages.places')}</h2>
					<ol className="grid gap-3">
						{stage.localities.map((locality) => (
							<LocalityCard key={locality.id} locality={locality} />
						))}
					</ol>
				</section>
			)}

			{stage.pointsOfInterest.length > 0 && (
				<section className="mt-8">
					<h2 className="mb-3 text-2xl font-semibold">{t('stages.pointsOfInterest')}</h2>
					<ul className="grid gap-3">
						{stage.pointsOfInterest.map((poi) => (
							<PoiCard key={poi.id} poi={poi} />
						))}
					</ul>
				</section>
			)}
		</Screen>
	)
}

// ¿El nombre oficial del municipio es el de la localidad? El INE da a veces los dos idiomas
// ("Pamplona/Iruña", "Orreaga/Roncesvalles"): basta con que coincida uno.
export const sameName = (municipalityName: string, localityName: string): boolean => {
	const norm = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase()
	return municipalityName.split('/').some((part) => norm(part) === norm(localityName))
}

// Foto de portada de la etapa: la primera de sus lugares de interés (las etapas no traen foto
// propia); null si ninguno tiene.
export const stagePhoto = (pois: PointOfInterest[]): string | null =>
	pois.find((poi) => poi.photoUrls.length > 0)?.photoUrls[0] ?? null

// Una localidad; si no es cabeza de su municipio (Zubiri, en Esteribar), se indica el municipio.
const LocalityCard = ({ locality }: { locality: StageLocality }) => {
	const { t } = useI18n()
	const municipality = locality.municipality
	return (
		<li className="rounded-3xl bg-white p-5 shadow-sm">
			<h3 className="text-xl font-bold">{locality.name}</h3>
			{municipality && !sameName(municipality.name, locality.name) && (
				<p className="text-earth-800">{t('stages.inMunicipality', { name: municipality.name })}</p>
			)}
			<p className="mt-1">{locality.description}</p>
			{locality.heritage && (
				<>
					<h4 className="mt-3 font-bold text-forest-800">{t('stages.heritage')}</h4>
					<p>{locality.heritage}</p>
				</>
			)}
			{locality.traditions && (
				<>
					<h4 className="mt-3 font-bold text-forest-800">{t('stages.traditions')}</h4>
					<p>{locality.traditions}</p>
				</>
			)}
			{locality.visitUrl && (
				<div className="mt-2">
					<ExternalLink href={locality.visitUrl}>{t('stages.moreInfo')}</ExternalLink>
				</div>
			)}
		</li>
	)
}

const PoiCard = ({ poi }: { poi: PointOfInterest }) => {
	const { t } = useI18n()
	return (
		<li>
			<Link
				href={`/lugares/${encodeURIComponent(poi.id)}`}
				className="flex items-center gap-3 rounded-3xl bg-white p-4 shadow-sm active:bg-earth-100"
			>
				{poi.photoUrls[0] && (
					<Photo src={poi.photoUrls[0]} alt="" className="size-24 shrink-0 rounded-2xl object-cover" />
				)}
				<div className="min-w-0 flex-1">
					<p className="text-sm font-semibold text-earth-800">{t(`poi.${poi.category}`)}</p>
					<h3 className="text-lg font-bold text-forest-800">{poi.name}</h3>
					<p className="mt-1 line-clamp-2 text-earth-800">{poi.description}</p>
				</div>
				<Icon name="forward" className="size-6 shrink-0 text-earth-600" />
			</Link>
		</li>
	)
}
